import CheckIcon from "../../../assets/svg/check-icon";
import {
  COMMUNICATION_CAMPAIGN_MODALITIES_OPTIONS,
  COMMUNICATION_FILTER_MODALITIES_OPTIONS,
} from "../../../pages/Communications/utils/constants";
import {
  clearCampaignCommunicationsOutcome,
  clearCampaignCommunicationsStatus,
  FilterState,
  selectCampaignCommunicationsFilters,
  selectCampaignExecutionLogsFilters,
  selectCommunicationsFilters,
  setAnalyticsModality,
  setCampaignCommunicationsMode,
  setCampaignExecutionLogsMode,
  setCommunicationAnalyticsMode,
  setCommunicationsMode,
} from "../../../redux/slices/filterSlice";
import Button from "../../Button/Button";
import { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getModalityDisplayValue } from "../utils/constants";
import { RootState } from "../../../redux/store";
const CommunicationModeFilter = ({
  onClose,
  modalities,
  isCampaignCommunication,
  executionLogs,
  communicationAnalytics,
  isAnalyticsBoard,
}: {
  onClose: () => void;
  modalities: string[];
  isCampaignCommunication?: boolean;
  executionLogs?: boolean;
  communicationAnalytics?: boolean;
  // The VerbaIQ/WebIQ board's "Modality" filter (design: one of 5 board filter fields) — reuses
  // this component rather than building a new one, same as the other boolean-flagged variants below.
  isAnalyticsBoard?: boolean;
}) => {
  const dispatch = useDispatch();
  const clientConfig = useSelector((store: RootState) => store.auth.client);
  const communicationMode = useSelector(
    isCampaignCommunication
      ? (state: { filter: FilterState }) =>
          selectCampaignCommunicationsFilters(state)?.mode
      : executionLogs
        ? (state: { filter: FilterState }) =>
            selectCampaignExecutionLogsFilters(state)?.mode
        : communicationAnalytics
          ? (state: { filter: FilterState }) =>
              state.filter.communicationAnalytics.mode
          : isAnalyticsBoard
            ? (state: { filter: FilterState }) => state.filter.analytics.modality
            : (state: { filter: FilterState }) =>
                selectCommunicationsFilters(state)?.mode,
  );

  const [selectedOption, setSelectedOption] = useState<string>(
    communicationMode.value as string,
  );

  const handleApply = () => {
    if (selectedOption === communicationMode.value) {
      return;
    }
    dispatch(
      isCampaignCommunication
        ? setCampaignCommunicationsMode({
            value: selectedOption,
            displayValue: getModalityDisplayValue(selectedOption),
          })
        : executionLogs
          ? setCampaignExecutionLogsMode({
              value: selectedOption,
              displayValue: getModalityDisplayValue(selectedOption),
            })
          : communicationAnalytics
            ? setCommunicationAnalyticsMode({
                value: selectedOption,
                displayValue: getModalityDisplayValue(selectedOption),
              })
            : isAnalyticsBoard
              ? setAnalyticsModality({
                  value: selectedOption,
                  displayValue: getModalityDisplayValue(selectedOption),
                })
              : setCommunicationsMode({
                  value: selectedOption,
                  displayValue: getModalityDisplayValue(selectedOption),
                }),
    );

    if (isCampaignCommunication) {
      dispatch(clearCampaignCommunicationsStatus());
      dispatch(clearCampaignCommunicationsOutcome());
    }

    onClose();
  };

  const filteredCampaignCommunicationsModalities = useMemo(
    () =>
      COMMUNICATION_FILTER_MODALITIES_OPTIONS?.filter(
        (modality) =>
          modality.value === "all" ||
          (modalities?.includes(modality.value) &&
            clientConfig?.live_communication_channels?.[modality.value]),
      ),
    [modalities, clientConfig?.live_communication_channels],
  );

  const filteredExecutionLogsModalities = useMemo(
    () =>
      COMMUNICATION_CAMPAIGN_MODALITIES_OPTIONS?.filter(
        (modality) =>
          modality?.value === "all" ||
          clientConfig?.live_communication_channels?.[modality.value],
      ),
    [clientConfig?.live_communication_channels],
  );

  const filteredCommunicationAnalyticsModalities = useMemo(
    () =>
      COMMUNICATION_FILTER_MODALITIES_OPTIONS?.filter(
        (modality) =>
          modality.value !== "all" &&
          clientConfig?.live_communication_channels?.[modality.value],
      ),
    [clientConfig?.live_communication_channels],
  );

  const filteredCommunicationsModalities = useMemo(
    () =>
      COMMUNICATION_FILTER_MODALITIES_OPTIONS?.filter(
        (modality) =>
          modality?.value === "all" ||
          clientConfig?.live_communication_channels?.[modality.value],
      ),
    [clientConfig?.live_communication_channels],
  );

  const modalitiesOptions = isCampaignCommunication
    ? filteredCampaignCommunicationsModalities
    : executionLogs
      ? filteredExecutionLogsModalities
      : communicationAnalytics
        ? filteredCommunicationAnalyticsModalities
        : filteredCommunicationsModalities;

  return (
    <div className="flex flex-col gap-1.5 mt-2 px-3">
      <h2 className="text-md  text-heading-text dark:text-heading-text-dk py-1.5">
        Select your preferred mode of communication.
      </h2>
      {modalitiesOptions?.map((option) => (
        <div
          key={option.value}
          onClick={() => setSelectedOption(option.value)}
          className={`flex items-center justify-between border cursor-pointer ${
            selectedOption === option.value
              ? "border-verba-blue dark:border-verba-blue-dk bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk"
              : "border-border-gray dark:border-border-gray-dk"
          } rounded-lg py-[13.5px] px-3`}
        >
          <div>{option.content}</div>
          {selectedOption === option.value && (
            <CheckIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
          )}
        </div>
      ))}
      <div className="flex justify-end gap-2 mt-4">
        <Button
          innerText="Cancel"
          onClick={onClose}
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
        />
        <Button
          onClick={handleApply}
          disabled={selectedOption === communicationMode.value}
          innerText="Apply"
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
        />
      </div>
    </div>
  );
};

export default CommunicationModeFilter;
